import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { Label, type LabelSize } from "./label.js";

type SelectSize = "sm" | "md" | "lg";

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  error?: string;
  helperText?: string;
  label?: React.ReactNode;
  optional?: boolean;
  size?: SelectSize;
}

export interface SelectOption {
  disabled?: boolean;
  label: string;
  value: string;
}

interface SelectFieldProps {
  disabled?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  label?: React.ReactNode;
  optional?: boolean;
  placeholder?: string;
  size?: SelectSize;
}

export interface MultiSelectProps extends SelectFieldProps {
  defaultValue?: string[];
  emptyText?: string;
  onValueChange?: (value: string[]) => void;
  options: SelectOption[];
  value?: string[];
}

export interface ComboboxProps extends SelectFieldProps {
  defaultValue?: string;
  emptyText?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  searchPlaceholder?: string;
  value?: string;
  /**
   * Controlled search text. Supply this together with `onSearchChange` when the
   * caller needs to see what the user typed — e.g. to filter a large source list
   * itself before handing a bounded `options` array down. Left undefined, the
   * Combobox keeps the query internally and filters `options` on `label`.
   */
  searchValue?: string;
  /** Fires on every keystroke in the search field. See `searchValue`. */
  onSearchChange?: (value: string) => void;
}

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

function ChevronIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
      <title>open</title>
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
      <title>selected</title>
      <path
        d="M3.5 8.25L6.5 11.25L12.5 4.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </svg>
  );
}

function getLabelSize(size: SelectSize): LabelSize {
  return size === "sm" ? "sm" : size === "lg" ? "lg" : "md";
}

function getSelectedLabel(options: SelectOption[], value: string | undefined) {
  return options.find((option) => option.value === value)?.label;
}

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(function SelectTrigger(
  {
    children,
    className,
    disabled,
    error,
    helperText,
    id,
    label,
    optional = false,
    size = "md",
    ...props
  },
  ref,
) {
  const generatedId = React.useId();
  const triggerId = id ?? generatedId;
  const helperId = helperText && !error ? `${triggerId}-helper` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={triggerId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}
      <SelectPrimitive.Trigger
        ref={ref}
        // Without a label *or* aria-label, the trigger has no accessible name
        // and axe flags button-name. Fall back so the field is always nameable.
        aria-label={!label ? "Select option" : undefined}
        aria-describedby={errorId ?? helperId}
        aria-invalid={error ? true : undefined}
        className={cx(
          "cw-v2-select-trigger",
          `cw-v2-select-trigger--${size}`,
          error && "cw-v2-select-trigger--error",
          disabled && "cw-v2-select-trigger--disabled",
          className,
        )}
        disabled={disabled}
        id={triggerId}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon className="cw-v2-select-trigger__icon">
          <ChevronIcon />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
SelectTrigger.displayName = "SelectTrigger";

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent(
  { align = "start", children, className, position = "popper", sideOffset = 6, style, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        align={align}
        className={cx("cw-v2-theme", "cw-v2-select-content", className)}
        position={position}
        sideOffset={sideOffset}
        // Inline override to defeat the Radix-Dialog body-pointer-events
        // lock — without this, Select.Content rendered inside a Dialog
        // (ModalForm / EditDrawer) silently swallows clicks, because the
        // Portal mounts under <body> which has pointer-events: none while
        // the dialog is open. CSS-level fix lives in components.css too,
        // but inline style is the safety net for any stale-bundle case.
        style={{ pointerEvents: "auto", ...style }}
        {...props}
      >
        <SelectPrimitive.Viewport className="cw-v2-select-viewport">
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = "SelectContent";

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectItem({ children, className, ...props }, ref) {
  return (
    <SelectPrimitive.Item ref={ref} className={cx("cw-v2-select-item", className)} {...props}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="cw-v2-select-item__indicator">
        <CheckIcon />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = "SelectItem";

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(function SelectLabel({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Label ref={ref} className={cx("cw-v2-select-label", className)} {...props} />
  );
});
SelectLabel.displayName = "SelectLabel";

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(function SelectSeparator({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cx("cw-v2-select-separator", className)}
      {...props}
    />
  );
});
SelectSeparator.displayName = "SelectSeparator";

/**
 * Multi-select using a Popover (not Radix Select, which is single-only).
 * Selected values render as removable chips inside the trigger.
 */
export function MultiSelect({
  defaultValue = [],
  disabled,
  emptyText = "No options available",
  error,
  helperText,
  id,
  label,
  onValueChange,
  optional = false,
  options,
  placeholder = "Select options",
  size = "md",
  value,
}: MultiSelectProps) {
  const generatedId = React.useId();
  const triggerId = id ?? generatedId;
  const helperId = helperText && !error ? `${triggerId}-helper` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const selectedValue = isControlled ? value : internalValue;
  const selectedOptions = selectedValue
    .map((selected) => options.find((option) => option.value === selected))
    .filter((option): option is SelectOption => Boolean(option));

  const updateValue = (nextValue: string[]) => {
    if (!isControlled) setInternalValue(nextValue);
    onValueChange?.(nextValue);
  };

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={triggerId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}
      <PopoverPrimitive.Root>
        <PopoverPrimitive.Trigger
          aria-label={!label ? "Select options" : undefined}
          aria-describedby={errorId ?? helperId}
          aria-invalid={error ? true : undefined}
          className={cx(
            "cw-v2-select-trigger",
            "cw-v2-select-trigger--multi",
            `cw-v2-select-trigger--${size}`,
            error && "cw-v2-select-trigger--error",
            disabled && "cw-v2-select-trigger--disabled",
          )}
          disabled={disabled}
          id={triggerId}
          type="button"
        >
          <span
            className={cx(
              "cw-v2-select-trigger__value",
              "cw-v2-select-trigger__value--chips",
              selectedOptions.length === 0 && "cw-v2-select-trigger__value--placeholder",
            )}
          >
            {selectedOptions.length > 0
              ? selectedOptions.map((option) => (
                  <span key={option.value} className="cw-v2-select-chip">
                    <span>{option.label}</span>
                    <span
                      aria-label={`Remove ${option.label}`}
                      className="cw-v2-select-chip__remove"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        updateValue(selectedValue.filter((item) => item !== option.value));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          updateValue(selectedValue.filter((item) => item !== option.value));
                        }
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      // biome-ignore lint/a11y/useSemanticElements: this span sits inside the Radix Popover trigger <button>; nested buttons aren't valid HTML, so the chip-remove uses role=button with full keyboard support.
                      role="button"
                      tabIndex={0}
                    >
                      <svg aria-hidden="true" viewBox="0 0 12 12">
                        <title>Remove</title>
                        <path
                          d="M3 3L9 9M9 3L3 9"
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </span>
                  </span>
                ))
              : placeholder}
          </span>
          <span className="cw-v2-select-trigger__icon">
            <ChevronIcon />
          </span>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            className="cw-v2-theme cw-v2-select-content"
            sideOffset={6}
          >
            <div className="cw-v2-select-viewport">
              {options.length > 0 ? (
                options.map((option) => {
                  const selected = selectedValue.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      className={cx("cw-v2-select-item", selected && "cw-v2-select-item--checked")}
                      disabled={option.disabled}
                      onClick={() => {
                        const nextValue = selected
                          ? selectedValue.filter((item) => item !== option.value)
                          : [...selectedValue, option.value];
                        updateValue(nextValue);
                      }}
                      type="button"
                    >
                      <span>{option.label}</span>
                      {selected ? (
                        <span className="cw-v2-select-item__indicator">
                          <CheckIcon />
                        </span>
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <div className="cw-v2-select-empty">{emptyText}</div>
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Single-select combobox (search-as-you-type). Uses a Popover + a
 * filtered list; the underlying value is a string.
 */
export function Combobox({
  defaultValue = "",
  disabled,
  emptyText = "No matching options",
  error,
  helperText,
  id,
  label,
  onValueChange,
  optional = false,
  options,
  placeholder = "Select option",
  searchPlaceholder = "Search options",
  searchValue,
  onSearchChange,
  size = "md",
  value,
}: ComboboxProps) {
  const generatedId = React.useId();
  const triggerId = id ?? generatedId;
  const helperId = helperText && !error ? `${triggerId}-helper` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;
  const isControlled = value !== undefined;
  const [open, setOpen] = React.useState(false);
  const [internalQuery, setInternalQuery] = React.useState("");
  // Controlled search: when the caller drives the text it is also responsible for
  // filtering, so `options` is rendered as given rather than filtered again here.
  const isSearchControlled = searchValue !== undefined;
  const query = isSearchControlled ? searchValue : internalQuery;
  const setQuery = (next: string) => {
    if (!isSearchControlled) setInternalQuery(next);
    onSearchChange?.(next);
  };
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const selectedValue = isControlled ? value : internalValue;
  const selectedLabel = getSelectedLabel(options, selectedValue);
  const filteredOptions = isSearchControlled
    ? options
    : options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));

  const updateValue = (nextValue: string) => {
    if (!isControlled) setInternalValue(nextValue);
    onValueChange?.(nextValue);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="cw-v2-input-field">
      {label ? (
        <Label htmlFor={triggerId} optional={optional} size={getLabelSize(size)}>
          {label}
        </Label>
      ) : null}
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger
          aria-describedby={errorId ?? helperId}
          aria-expanded={open}
          aria-invalid={error ? true : undefined}
          aria-label={!label ? "Select option" : undefined}
          className={cx(
            "cw-v2-select-trigger",
            `cw-v2-select-trigger--${size}`,
            error && "cw-v2-select-trigger--error",
            disabled && "cw-v2-select-trigger--disabled",
          )}
          disabled={disabled}
          id={triggerId}
          // biome-ignore lint/a11y/useSemanticElements: native <select> can't host an embedded search input; the WAI-ARIA combobox pattern requires role=combobox on a <button>.
          role="combobox"
          type="button"
        >
          <span
            className={cx(
              "cw-v2-select-trigger__value",
              !selectedLabel && "cw-v2-select-trigger__value--placeholder",
            )}
          >
            {selectedLabel ?? placeholder}
          </span>
          <span className="cw-v2-select-trigger__icon">
            <ChevronIcon />
          </span>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            className="cw-v2-theme cw-v2-select-content"
            sideOffset={6}
          >
            <div className="cw-v2-combobox-search">
              <input
                aria-label={searchPlaceholder}
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                className="cw-v2-combobox-search__input"
                enterKeyHint="search"
                inputMode="search"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                spellCheck={false}
                type="search"
                value={query}
              />
            </div>
            <div className="cw-v2-select-viewport">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const selected = selectedValue === option.value;
                  return (
                    <button
                      key={option.value}
                      className={cx("cw-v2-select-item", selected && "cw-v2-select-item--checked")}
                      disabled={option.disabled}
                      onClick={() => updateValue(option.value)}
                      type="button"
                    >
                      <span>{option.label}</span>
                      {selected ? (
                        <span className="cw-v2-select-item__indicator">
                          <CheckIcon />
                        </span>
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <div className="cw-v2-select-empty">{emptyText}</div>
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      {error ? (
        <p className="cw-v2-input__message cw-v2-input__message--error" id={errorId}>
          {error}
        </p>
      ) : helperText ? (
        <p className="cw-v2-input__message" id={helperId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
